// Дашборд адмінки: ключові метрики проєкту в одному місці.

import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { useFetch } from '../../lib/useFetch';
import { Loader } from '../../components/Loader';
import { ErrorView } from '../../components/ErrorView';

export function AdminDashboard() {
  const { data, loading, error, reload } = useFetch(() => api.admin.stats(), []);

  if (loading) return <Loader />;
  if (error) return <ErrorView error={error} onRetry={reload} />;
  if (!data) return null;

  const unmarkedPct =
    data.questions.total > 0
      ? Math.round((data.questions.unmarked / data.questions.total) * 100)
      : 0;

  return (
    <div className="space-y-4 p-4">
      {/* Виручка */}
      <Section title="💳 Виручка">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Сьогодні" value={`${data.revenue.today} ₴`} accent="success" hint={`${data.revenue.purchasesToday} продажів`} />
          <Stat label="За тиждень" value={`${data.revenue.week} ₴`} hint={`${data.revenue.purchasesWeek} продажів`} />
          <Stat label="За 30 днів" value={`${data.revenue.month} ₴`} hint={`${data.revenue.purchasesMonth} продажів`} />
          <Stat label="Усього" value={`${data.revenue.total} ₴`} hint={`${data.revenue.purchasesTotal} продажів`} />
        </div>
        {data.revenue.byPlan.length > 0 && (
          <div className="mt-3 space-y-1 text-xs">
            <div className="font-semibold text-tg-hint">За тарифом (30 днів):</div>
            {data.revenue.byPlan.map((p) => (
              <div key={p.months} className="flex items-center justify-between rounded bg-tg-bg px-3 py-2">
                <span>{p.months} {p.months === 1 ? 'місяць' : (p.months < 5 ? 'місяці' : 'місяців')}</span>
                <span className="text-tg-text">
                  <strong>{p.count}</strong> × <span className="text-tg-hint">{p.sumUah} ₴</span>
                </span>
              </div>
            ))}
            <div className="mt-2 text-tg-hint">Середній чек: <strong className="text-tg-text">{data.revenue.avg30d} ₴</strong></div>
          </div>
        )}
      </Section>

      {/* Premium */}
      <Section title="👑 Premium">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          <Stat label="Активних підписок" value={data.premium.active} accent="success" />
          <Stat
            label="Закінчуються за 7 днів"
            value={data.premium.expiringIn7Days}
            accent={data.premium.expiringIn7Days > 0 ? 'warning' : undefined}
            hint="час нагадати"
          />
          <Stat
            label="% активних з усіх"
            value={`${data.users.total > 0 ? Math.round((data.premium.active / data.users.total) * 100) : 0}%`}
          />
        </div>
      </Section>

      {/* Воронка конверсії */}
      <Section title="📈 Воронка конверсії">
        <div className="space-y-2">
          <FunnelStep label="Зареєструвалися" value={data.conversion.registered} pct={100} />
          <FunnelStep
            label="Пройшли хоча б 1 іспит"
            value={data.conversion.triedExam}
            pct={data.conversion.tryRate}
            hint={`${data.conversion.tryRate}% від реєстрацій`}
          />
          <FunnelStep
            label="Купили Premium"
            value={data.conversion.boughtPremium}
            pct={data.conversion.overallRate}
            hint={`${data.conversion.buyRate}% від тих, хто пробував · ${data.conversion.overallRate}% від усіх`}
            accent="success"
          />
        </div>
      </Section>

      {/* 🟢 Хто ЗАРАЗ у боті — оновлюється при кожному запиті Mini App */}
      <Section title="🟢 Зараз у боті">
        <div className="grid grid-cols-3 gap-2">
          <Stat
            label="Онлайн"
            value={data.users.onlineNow ?? 0}
            hint="активні зараз (5 хв)"
            accent="success"
          />
          <Stat
            label="За 15 хв"
            value={data.users.online15 ?? 0}
            hint="заходили нещодавно"
          />
          <Stat
            label="За годину"
            value={data.users.onlineHour ?? 0}
            hint="активні в боті"
          />
        </div>
      </Section>

      {/* Користувачі */}
      <Section title="👥 Користувачі">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Всього" value={data.users.total} />
          <Stat label="Нових за 7 днів" value={data.users.newLast7Days} accent="success" />
          <Stat label="DAU" value={data.users.dau} hint="за 24 год" />
          <Stat label="WAU" value={data.users.wau} hint="за 7 днів" />
        </div>
      </Section>

      {/* Платформи */}
      {data.platforms && (
        <Section title="📲 Платформи">
          <div className="grid grid-cols-3 gap-2">
            <Stat
              label="Telegram"
              value={data.platforms.telegram.total}
              hint={`${data.platforms.telegram.wau} акт. за 7 дн`}
            />
            <Stat
              label="Сайт (пошта/Google)"
              value={data.platforms.site.total}
              hint={`${data.platforms.site.wau} акт. за 7 дн`}
            />
            <Stat
              label="Android-додаток"
              value={data.platforms.app.total}
              hint={`${data.platforms.app.wau} акт. за 7 дн`}
              accent="success"
            />
          </div>
          <p className="mt-2 text-[11px] text-tg-hint">
            Групи перетинаються: юзер з Telegram міг поставити й додаток. «Додаток» = отримав
            бонус за встановлення або має токен сповіщень.
          </p>
        </Section>
      )}

      {/* Активність */}
      <Section title="🎯 Активність">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Тестів усього" value={data.attempts.total} />
          <Stat label="Сьогодні" value={data.attempts.today} accent="success" />
          <Stat label="За тиждень" value={data.attempts.week} />
          <Stat label="% здачі" value={`${data.attempts.passRate}%`} hint={`${data.attempts.passed} здано`} />
        </div>
      </Section>

      {/* Питання */}
      <Section title="📝 Питання">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Усього" value={data.questions.total} />
          <Stat label="Активних" value={data.questions.active} accent="success" />
          <Stat label="З картинкою" value={data.questions.withImage} />
          <Stat
            label="Без правильної відповіді"
            value={data.questions.unmarked}
            accent={unmarkedPct > 50 ? 'destructive' : unmarkedPct > 10 ? 'warning' : 'success'}
            hint={`${unmarkedPct}%`}
          />
        </div>
        {data.questions.unmarked > 0 && (
          <Link
            to="/admin/questions/mark"
            className="btn-primary mt-2 block w-full text-center"
          >
            ⚡ Розмітити правильні відповіді ({data.questions.unmarked})
          </Link>
        )}
      </Section>

      {/* Битви */}
      <Section title="⚔️ Битви">
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
          <Stat label="Всього" value={data.matches.total} />
          <Stat label="Активних" value={data.matches.active} accent="success" />
          <Stat label="Завершено" value={data.matches.finished} />
          <Stat label="Нічиїх" value={data.matches.draws} />
        </div>
      </Section>

      {/* Закладки */}
      <Section title="💚 Закладки">
        <Stat label="Усього збережень" value={data.bookmarks} />
      </Section>

      {/* Топ категорій */}
      {data.topCategories.length > 0 && (
        <Section title="🏆 Найпопулярніші теми">
          <div className="space-y-1">
            {data.topCategories.map((c, i) => (
              <div
                key={c.categoryId ?? i}
                className="flex items-center justify-between rounded-lg bg-tg-bg px-3 py-2 text-sm"
              >
                <span className="truncate">
                  <span className="text-tg-hint">#{i + 1}</span> {c.nameUk}
                </span>
                <span className="font-semibold">{c.attempts}</span>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Найскладніші питання — куди звернути увагу */}
      {data.mostMistaken.length > 0 && (
        <Section title="🔥 Найбільше помилок">
          <div className="space-y-1">
            {data.mostMistaken.map((q, i) => (
              <Link
                key={q.questionId}
                to={`/admin/questions/${q.questionId}`}
                className="flex items-center justify-between gap-2 rounded-lg bg-tg-bg px-3 py-2 text-sm hover:bg-tg-bg/60"
              >
                <span className="min-w-0 flex-1 truncate">
                  <span className="text-tg-hint">#{i + 1}</span>{' '}
                  {q.order > 0 && <span className="font-semibold text-tg-link">{q.order}.</span>}{' '}
                  {q.textUk}
                </span>
                <span className="shrink-0 font-semibold text-tg-destructive">
                  ✗ {q.wrongCount}
                </span>
              </Link>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="card space-y-2">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-tg-hint">{title}</h3>
      {children}
    </section>
  );
}

function FunnelStep({
  label,
  value,
  pct,
  hint,
  accent,
}: {
  label: string;
  value: number;
  pct: number;
  hint?: string;
  accent?: 'success';
}) {
  const barColor = accent === 'success' ? 'bg-tg-success' : 'bg-tg-link';
  return (
    <div className="rounded-lg bg-tg-bg p-3">
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="font-medium">{label}</span>
        <span className="font-semibold">{value}</span>
      </div>
      <div className="h-2 overflow-hidden rounded-full bg-tg-secondary-bg">
        <div className={`h-full ${barColor}`} style={{ width: `${Math.min(100, pct)}%` }} />
      </div>
      {hint && <div className="mt-1 text-[11px] text-tg-hint">{hint}</div>}
    </div>
  );
}

function Stat({
  label,
  value,
  accent,
  hint,
}: {
  label: string;
  value: number | string;
  accent?: 'success' | 'destructive' | 'warning';
  hint?: string;
}) {
  const cls =
    accent === 'success'
      ? 'text-tg-success'
      : accent === 'destructive'
        ? 'text-tg-destructive'
        : accent === 'warning'
          ? 'text-yellow-500'
          : '';
  return (
    <div className="rounded-lg bg-tg-bg p-3">
      <div className={`text-xl font-semibold ${cls}`}>{value}</div>
      <div className="text-xs text-tg-hint">{label}</div>
      {hint && <div className="mt-0.5 text-[10px] text-tg-hint">{hint}</div>}
    </div>
  );
}
